import {ILanguageData, IPluginData, ITagData} from "@hooks/plugins/IPluginsDataResponse";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {LanguageOption, TagOption} from "@components/elements/FiltersToolbar";

const SECONDS_IN_DAY = 60 * 60 * 24;

const safeDivide = (numerator?: number | null, denominator?: number | null): number | null => {
    if (numerator === null || numerator === undefined) return null;
    if (!denominator) return null;
    const result = numerator / denominator;
    return Number.isFinite(result) ? result : null;
};

const computeAgeDaysFromTimestamp = (timestamp?: number, nowMs?: number): number | null => {
    if (!timestamp) return null;
    const referenceMs = typeof nowMs === "number" && Number.isFinite(nowMs) ? nowMs : Date.now();
    const ageInSeconds = (referenceMs / 1000) - timestamp;
    if (!Number.isFinite(ageInSeconds) || ageInSeconds < 0) {
        return null;
    }
    return ageInSeconds / SECONDS_IN_DAY;
};

const sumCounts = <T extends { count?: number }>(items: T[] = []): number => {
    if (!items.length) return 0;
    const total = items.reduce((sum, item) => sum + (item.count ?? 0), 0);
    return total || items.length;
};

export class Plugin {
    static processApiResponse(pluginAPIData: IPluginData, nowMs?: number): IProcessedPluginData {
        const ageDays = pluginAPIData.age_days ?? computeAgeDaysFromTimestamp(pluginAPIData.added?.timestamp, nowMs);
        const installsPerRating = pluginAPIData.installs_per_rating ?? safeDivide(pluginAPIData.active_installs, pluginAPIData.num_ratings);
        const downloadsPerRating = pluginAPIData.downloads_per_rating ?? safeDivide(pluginAPIData.downloaded, pluginAPIData.num_ratings);
        const installsPerDay = pluginAPIData.installs_per_day ?? safeDivide(pluginAPIData.active_installs, ageDays);
        const downloadsPerDay = pluginAPIData.downloads_per_day ?? safeDivide(pluginAPIData.downloaded, ageDays);
        const reviewsPerDay = pluginAPIData.reviews_per_day ?? safeDivide(pluginAPIData.num_ratings, ageDays);
        const activationRate = pluginAPIData.activation_rate ?? safeDivide(pluginAPIData.active_installs, pluginAPIData.downloaded);
        const tagsCount = pluginAPIData.tags_count ?? sumCounts(pluginAPIData.tags);
        const languagesCount = pluginAPIData.languages_count ?? sumCounts(pluginAPIData.languages);

        return {
            // TODO: set ID to field pluginid when API is updated
            active_installs: pluginAPIData.active_installs,
            added: new Date(pluginAPIData.added.timestamp * 1000),
            author: pluginAPIData.author,
            htmlStrippedAuthor: pluginAPIData.author?.replace(/(<([^>]+)>)/gi, ""),
            author_profile: pluginAPIData.author_profile,
            banners: pluginAPIData.banners,
            changelog: pluginAPIData.changelog ?? '',
            htmlStrippedChangelog: pluginAPIData.changelog?.replace(/(<([^>]+)>)/gi, ""),
            contributors: pluginAPIData.contributors,
            description: pluginAPIData.description,
            htmlStrippedDescription: pluginAPIData.description?.replace(/(<([^>]+)>)/gi, ""),
            downloaded: pluginAPIData.downloaded,
            faq: pluginAPIData.faq,
            htmlStrippedFaq: pluginAPIData.faq?.replace(/(<([^>]+)>)/gi, ""),
            homepage: pluginAPIData.homepage,
            icons: pluginAPIData.icons,
            installation: pluginAPIData.installation ?? '',
            htmlStrippedInstallation: pluginAPIData.installation?.replace(/(<([^>]+)>)/gi, ""),
            internal_ranking: pluginAPIData.internal_ranking,
            languages: pluginAPIData.languages.map((language: ILanguageData): LanguageOption => new LanguageOption(language.slug, language.label, language.count)),
            last_seen: new Date(pluginAPIData.last_seen.timestamp * 1000),
            last_updated: new Date(pluginAPIData.last_updated.timestamp * 1000),
            last_wordpress_compatible_version: pluginAPIData.last_wordpress_compatible_version,
            latest_num_ratings: pluginAPIData.latest_num_ratings,
            latest_rating: pluginAPIData.latest_rating,
            latest_ratings: pluginAPIData.latest_ratings,
            minimum_php_version: pluginAPIData.minimum_php_version,
            minimum_wordpress_version: pluginAPIData.minimum_wordpress_version,
            name: pluginAPIData.name,
            num_ratings: pluginAPIData.num_ratings,
            pluginid: pluginAPIData.pluginid,
            rating: pluginAPIData.rating,
            ratings: pluginAPIData.ratings,
            reviews: pluginAPIData.reviews,
            screenshots: pluginAPIData.screenshots,
            htmlStrippedScreenshots: pluginAPIData.screenshots?.replace(/(<([^>]+)>)/gi, ""),
            short_description: pluginAPIData.short_description,
            support_threads: pluginAPIData.support_threads,
            support_threads_resolved: pluginAPIData.support_threads_resolved,
            tags: pluginAPIData.tags.map((tag: ITagData): TagOption => new TagOption(tag.slug, tag.label, tag.count)),
            tags_count: tagsCount,
            version: pluginAPIData.version,
            wordpress_link: pluginAPIData.wordpress_link,
            age_days: ageDays,
            installs_per_rating: installsPerRating,
            downloads_per_rating: downloadsPerRating,
            installs_per_day: installsPerDay,
            downloads_per_day: downloadsPerDay,
            reviews_per_day: reviewsPerDay,
            activation_rate: activationRate,
            languages_count: languagesCount,
            marketplace: pluginAPIData.marketplace,
            marketplace_url: pluginAPIData.marketplace_url,
            price: pluginAPIData.price,
        }
    }
}
